import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import InventoryIcon from '@mui/icons-material/Inventory';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { adminApi, DashboardMetrics, productApi } from '../../api/admin';
import {
  downloadExcelBuffer,
  formatDateForExport,
  styleExcelHeaderRow,
  todayFileStamp,
  writeLandscapePdfTable,
} from '../../utils/adminReportExport';

interface MetricsDashboardProps {
  detailed?: boolean;
}

const MetricsDashboard: React.FC<MetricsDashboardProps> = ({ detailed = false }) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [productsCount, setProductsCount] = useState<number>(0);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    loadMetrics();
    loadProductsCount();
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getDashboardMetrics();
      setMetrics(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const loadProductsCount = async () => {
    setLoadingProducts(true);
    try {
      const products = await productApi.getAllProducts();
      setProductsCount(products.length);
    } catch (err: any) {
      console.error('Failed to load products count:', err);
      setProductsCount(0);
    } finally {
      setLoadingProducts(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getMetricsRows = () => {
    if (!metrics) return [];
    const rows: Array<[string, string]> = [
      ['Total Clients', String(metrics.totalClients || 0)],
      ['Total Loans', String(metrics.totalLoans || 0)],
      ['Active Loans', String(metrics.activeLoans || 0)],
      ['Total Products', String(productsCount)],
      ['Active ZIG Rate', metrics.activeZigRate?.rate != null ? String(metrics.activeZigRate.rate) : 'N/A'],
      [
        'ZIG Effective Date',
        metrics.activeZigRate?.effectiveDate
          ? formatDateForExport(metrics.activeZigRate.effectiveDate)
          : 'N/A',
      ],
    ];
    if (detailed) {
      rows.push(
        ['Total Disbursed', formatCurrency(metrics.totalDisbursed || 0)],
        ['Total Outstanding', formatCurrency(metrics.totalOutstanding || 0)]
      );
    }
    return rows;
  };

  const exportToPDF = () => {
    writeLandscapePdfTable({
      title: 'Dashboard Metrics Report',
      subtitle: detailed ? 'Detailed analytics snapshot' : 'Overview snapshot',
      headers: ['Metric', 'Value'],
      colWidths: [80, 80],
      rows: getMetricsRows(),
      fileName: `metrics-report-${todayFileStamp()}.pdf`,
    });
  };

  const exportToExcel = async () => {
    if (!metrics) return;
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Metrics');

      worksheet.columns = [
        { header: 'Metric', key: 'metric', width: 30 },
        { header: 'Value', key: 'value', width: 40 },
      ];
      styleExcelHeaderRow(worksheet);

      getMetricsRows().forEach(([metric, value]) => {
        worksheet.addRow({ metric, value });
      });

      const metadataSheet = workbook.addWorksheet('Export Info');
      metadataSheet.columns = [
        { header: 'Property', key: 'property', width: 25 },
        { header: 'Value', key: 'value', width: 50 },
      ];
      styleExcelHeaderRow(metadataSheet);
      metadataSheet.addRows([
        { property: 'Export Date', value: new Date().toLocaleString() },
        { property: 'Report Type', value: detailed ? 'Detailed' : 'Overview' },
      ]);

      const buffer = await workbook.xlsx.writeBuffer();
      await downloadExcelBuffer(buffer, `metrics-report-${todayFileStamp()}.xlsx`);
    } catch (err) {
      console.error('Failed to export Excel:', err);
      alert('Failed to export Excel. Please ensure exceljs is installed.');
    }
  };

  if (loading && !metrics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
        {error}
      </Alert>
    );
  }

  if (!metrics) {
    return (
      <Alert severity="info">
        No metrics available. Click refresh to load data.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Dashboard Metrics</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            disabled={loading || !metrics}
            color="error"
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={exportToExcel}
            disabled={loading || !metrics}
            color="success"
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadMetrics}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Clients
                  </Typography>
                  <Typography variant="h4">{metrics.totalClients || 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Loans
                  </Typography>
                  <Typography variant="h4">{metrics.totalLoans || 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Loans
                  </Typography>
                  <Typography variant="h4">{metrics.activeLoans || 0}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CurrencyExchangeIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active ZIG Rate
                  </Typography>
                  <Typography variant="h4">
                    {metrics.activeZigRate?.rate || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InventoryIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Products
                  </Typography>
                  <Typography variant="h4">
                    {loadingProducts ? '...' : productsCount}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {detailed && (
          <>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Disbursed
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(metrics.totalDisbursed || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Outstanding
                  </Typography>
                  <Typography variant="h4" color="error">
                    {formatCurrency(metrics.totalOutstanding || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {metrics.activeZigRate && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Active ZIG Rate Information
                  </Typography>
                  <Typography variant="body1">
                    Rate: 1 USD = {metrics.activeZigRate.rate} ZIG
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Effective Date:{' '}
                    {metrics.activeZigRate.effectiveDate
                      ? new Date(metrics.activeZigRate.effectiveDate).toLocaleString()
                      : 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};

export default MetricsDashboard;

